import { Sparkles, Ruler, ChefHat, Bath, Trees, ArrowRight } from "lucide-react";

/**
 * Decorative SpaceCost product mockup — drop-in replacement for the stock
 * `<img>` on the SpaceCost umbrella tool card.
 *
 * Intentionally tilted on the same axis as the FlipCost product image so
 * the two cards read as a matched pair on the MyPlanCost umbrella page.
 *
 * Pure presentational: no inputs, no analytics, no router. The whole
 * card swallows pointer events so the parent ToolCard's onClick still
 * fires when this is rendered inside it.
 */
export default function SpaceCostMockup() {
  return (
    <div className="absolute inset-0 bg-[#0F0E0C] overflow-hidden" aria-hidden="true" data-testid="spacecost-mockup">
      {/* Soft mint glow behind the tilted card to match FlipCost's depth */}
      <div className="absolute -inset-10 bg-[radial-gradient(circle_at_70%_30%,rgba(94,234,212,0.18),transparent_60%)]" />
      <div className="absolute -inset-10 bg-[radial-gradient(circle_at_20%_80%,rgba(15,118,110,0.15),transparent_55%)]" />

      {/* Tilted product card */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{ perspective: "1200px" }}
      >
        <div
          className="bg-white text-[#1C1C1C] rounded-xl shadow-2xl w-[140%] -translate-y-3 sm:-translate-y-5 px-7 py-7 sm:px-9 sm:py-9"
          style={{
            transform: "rotateX(8deg) rotateY(-12deg) rotateZ(2deg)",
            transformOrigin: "60% 50%",
          }}
          data-theme="spacecost"
        >
          <div className="grid grid-cols-12 gap-5 items-start">
            {/* Left column — brand + headline */}
            <div className="col-span-7 min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="fc-heading text-[18px] sm:text-[22px] font-extrabold tracking-tight">SpaceCost</span>
                <span className="text-[8px] sm:text-[9px] uppercase tracking-[0.22em] text-[#A8A29E] font-medium">
                  by myplancost
                </span>
              </div>
              <div className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.16em] text-sage-700 bg-sage-100 border border-sage-200 rounded-full px-2 py-0.5 mt-2">
                <Sparkles className="w-2.5 h-2.5" /> Plan before you build
              </div>
              <div className="fc-heading text-[24px] sm:text-[34px] leading-[1.05] tracking-tight mt-3 sm:mt-4">
                Cost a single space —
                <br />
                before you swing the
                <br />
                first hammer.
              </div>

              {/* Buttons — non-interactive but visually anchored */}
              <div className="mt-5 sm:mt-6 flex items-center gap-2.5">
                <div className="bg-[#1C1C1C] text-white rounded-lg h-8 sm:h-9 px-3 sm:px-4 text-[11px] sm:text-[12px] font-medium inline-flex items-center gap-1.5">
                  Start a project <ArrowRight className="w-3 h-3" />
                </div>
                <div className="text-[11px] sm:text-[12px] text-[#57534E] inline-flex items-center gap-1.5 px-2 sm:px-3 h-8 sm:h-9 border border-black/[0.08] rounded-lg bg-white">
                  Try a quick estimate
                </div>
              </div>
            </div>

            {/* Right column — controls panel */}
            <div className="col-span-5 space-y-3 sm:space-y-4">
              {/* Space size slider */}
              <div className="border border-black/[0.06] rounded-md p-2.5 sm:p-3">
                <div className="text-[8px] uppercase tracking-[0.16em] text-[#A8A29E]">Space size</div>
                <div className="flex items-end gap-1 mt-1">
                  <span className="fc-heading text-[18px] sm:text-[22px] leading-none">12</span>
                  <span className="text-[10px] text-[#78716C] mb-[2px]">sqm</span>
                </div>
                <div className="mt-2 h-1.5 bg-black/[0.08] rounded-full">
                  <div className="h-1.5 w-[35%] bg-sage-300 rounded-full" />
                </div>
              </div>

              {/* Room type pills */}
              <div className="border border-black/[0.06] rounded-md p-2.5 sm:p-3">
                <div className="text-[8px] uppercase tracking-[0.16em] text-[#A8A29E]">Room type</div>
                <div className="flex gap-1.5 flex-wrap mt-1.5">
                  <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-sage-100 border border-sage-300 text-sage-700">
                    <ChefHat className="w-2.5 h-2.5" /> Kitchen
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-black/[0.08] text-[#78716C]">
                    <Bath className="w-2.5 h-2.5" /> Bath
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-black/[0.08] text-[#78716C]">
                    <Trees className="w-2.5 h-2.5" /> Garden
                  </span>
                </div>
              </div>

              {/* Estimated total */}
              <div className="rounded-md p-2.5 sm:p-3 bg-sage-50 border border-sage-200">
                <div className="text-[8px] uppercase tracking-[0.16em] text-sage-700">Estimated total</div>
                <div className="fc-heading text-[20px] sm:text-[24px] leading-none mt-1">R148,500</div>
                <div className="text-[10px] text-[#78716C] mt-1 inline-flex items-center gap-1">
                  <Ruler className="w-2.5 h-2.5" /> 9 categories · 23 line items
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Subtle vignette to anchor the bottom of the card */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#1A1917] via-[#1A1917]/40 to-transparent pointer-events-none" />
    </div>
  );
}
